<template>
  <div class="editor-view">
    <JobProgress
      ref="jobProgress"
      :job-id="currentJobId"
      :title="$t('editor.generatingPdf')"
      @completed="handleJobCompleted"
      @failed="handleJobFailed"
      @closed="handleJobClosed"
    />
    
    <!-- New streamlined flow (DeepSeek-inspired hero + dual CTAs) -->
    <div class="streamlined-flow" v-if="!showLegacyEditor">
      <div class="flow-header">
        <p class="flow-tagline">{{ $t('homepage.flowTagline') }}</p>
        <h1 class="flow-title" data-testid="homepage-flow-title">{{ $t('homepage.flowTitle') }}</h1>
        <p class="flow-subtitle">{{ $t('homepage.flowSubtitle') }}</p>
      </div>
      
      <div class="flow-ctas">
        <router-link to="/job-recommendations" class="cta-card cta-primary" data-testid="hero-job-recommendations">
          <span class="cta-label">{{ $t('homepage.ctaJobRecommendations') }}</span>
          <p class="cta-desc">{{ $t('homepage.ctaJobRecommendationsDesc') }}</p>
        </router-link>
        <router-link to="/job-recommendations?mode=skill-advice" class="cta-card cta-secondary" data-testid="hero-skill-advice">
          <span class="cta-label">{{ $t('homepage.ctaSkillAdvice') }}</span>
          <p class="cta-desc">{{ $t('homepage.ctaSkillAdviceDesc') }}</p>
        </router-link>
        <router-link to="/interview-debrief" class="cta-card cta-secondary" data-testid="hero-interview-debrief">
          <span class="cta-label">{{ $t('homepage.ctaInterviewDebrief') }}</span>
          <p class="cta-desc">{{ $t('homepage.ctaInterviewDebriefDesc') }}</p>
        </router-link>
      </div>
      
      <div class="flow-info">
        <div class="info-card">
          <h3>{{ $t('homepage.howItWorksTitle') }}</h3>
          <ol>
            <li>{{ $t('homepage.howItWorksStep1') }}</li>
            <li>{{ $t('homepage.howItWorksStep2') }}</li>
            <li>{{ $t('homepage.howItWorksStep3') }}</li>
          </ol>
        </div>
        <div class="info-card">
          <h3>{{ $t('homepage.benefitsTitle') }}</h3>
          <ul>
            <li>{{ $t('homepage.benefitUploadNoLogin') }}</li>
            <li>{{ $t('homepage.benefitJobExplanations') }}</li>
            <li>{{ $t('homepage.benefitSkillAdvice') }}</li>
            <li>{{ $t('homepage.benefitAIGuidance') }}</li>
          </ul>
        </div>
      </div>
      
      <div class="legacy-toggle">
        <button @click="toggleLegacyEditor" class="btn-legacy">
          {{ $t('homepage.legacyEditor') }}
        </button>
      </div>
    </div>
    
    <!-- Legacy editor flow (for backward compatibility) -->
    <div v-else>
      <div class="upload-section" v-if="!resumeMarkdown">
        <PdfUpload @upload-success="handleUploadSuccess" />
      </div>
      
      <div class="editor-container" v-else>
        <div class="editor-header">
          <h2>{{ $t('editor.resumeEditor') }}</h2>
          <div class="header-actions">
            <button @click="toggleLegacyEditor" class="btn btn-secondary">
              <span class="btn-icon">🚀</span> {{ $t('editor.tryNewFlow') }}
            </button>
            <FunnyTransformButton
              :session-id="sessionId"
              :markdown="resumeMarkdown"
              :disabled="!resumeMarkdown || resumeMarkdown.trim().length < 50"
              intensity="medium"
              @transform-success="handleTransformationSuccess"
              @transform-error="handleTransformationError"
              class="funny-transform-btn"
            />
            <button @click="generatePdf" class="btn btn-primary" :disabled="pdfGenerating">
              <span class="btn-icon">📄</span> {{ $t('editor.exportPdf') }}
            </button>
            <button @click="downloadGeneratedPdf" class="btn btn-success" :disabled="!lastGeneratedJobId">
              <span class="btn-icon">⬇️</span> {{ $t('editor.downloadPdf') }}
            </button>
            <button @click="resetEditor" class="btn btn-secondary">
              <span class="btn-icon">🔄</span> {{ $t('editor.newResume') }}
            </button>
          </div>
        </div>
        
        <div class="editor-layout">
          <div class="editor-column">
            <div class="editor-section">
              <h3>{{ $t('editor.resumeContent') }}</h3>
              <ResumeEditor 
                v-model="resumeMarkdown"
                @text-selected="handleTextSelected"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { toast } from 'vue3-toastify'
import PdfUpload from '../components/PdfUpload.vue'
import ResumeEditor from '../components/ResumeEditor.vue'
import JobProgress from '../components/JobProgress.vue'
import FunnyTransformButton from '../components/FunnyTransformButton.vue'
import api from '../services/api'
import { useAuthStore } from '../stores/auth'

export default {
  name: 'EditorView',
  components: {
    PdfUpload,
    ResumeEditor,
    JobProgress,
    FunnyTransformButton
  },
  setup() {
    const route = useRoute()
    const authStore = useAuthStore()
    
    const resumeMarkdown = ref('')
    const sessionId = ref('')
    const selectedText = ref('')
    const currentJobId = ref('')
    const pdfGenerating = ref(false)
    const jobProgress = ref(null)
    const lastGeneratedJobId = ref('')
    const lastGeneratedTimestamp = ref('')
    
    const showLegacyEditor = ref(false)
    const revealDone = ref(false)

    // Component mounted
    onMounted(() => {
      // Check for humorous resume from agent (Job Recommendations flow)
      try {
        const fromAgent = sessionStorage.getItem('humorousResumeFromAgent')
        if (fromAgent) {
          sessionStorage.removeItem('humorousResumeFromAgent')
          resumeMarkdown.value = fromAgent
          showLegacyEditor.value = true
        }
      } catch (_) {}

      // Check if user wants legacy editor (from localStorage or URL param)
      if (!showLegacyEditor.value) {
        const legacyParam = route.query.legacy
        if (legacyParam === 'true') {
          showLegacyEditor.value = true
        } else {
          const savedPreference = localStorage.getItem('editorPreference')
          showLegacyEditor.value = savedPreference === 'legacy'
        }
      }
      // Trigger staggered reveal (CSS handles animation-delay)
      if (!showLegacyEditor.value) {
        requestAnimationFrame(() => {
          setTimeout(() => { revealDone.value = true }, 50)
        })
      }
    })

    const toggleLegacyEditor = () => {
      showLegacyEditor.value = !showLegacyEditor.value
      localStorage.setItem('editorPreference', showLegacyEditor.value ? 'legacy' : 'streamlined')
      
      if (showLegacyEditor.value) {
        toast.info('Switched to legacy editor')
      } else {
        toast.success('Switched to streamlined flow')
      }
    }
    
    const handleUploadSuccess = (data) => {
      resumeMarkdown.value = data.markdown
      sessionId.value = data.sessionId
      toast.success('Resume uploaded and processed successfully!')
    }

    const handleTextSelected = (text) => {
      selectedText.value = text
    }

    const generatePdf = async () => {
      pdfGenerating.value = true
      try {
        console.log('DEBUG: Starting PDF generation...')
        console.log('DEBUG: sessionId:', sessionId.value)
        console.log('DEBUG: resumeMarkdown length:', resumeMarkdown.value?.length)
        
        // Use async mode by default for better timeout handling
        const response = await api.generatePdf(sessionId.value, resumeMarkdown.value, true)
        console.log('DEBUG: API response:', response)
        
        // Check if response is a job (async mode) or PDF buffer (sync mode)
        if (response.jobId) {
          // Async job created - show progress modal
          console.log('DEBUG: Async job created, jobId:', response.jobId)
          currentJobId.value = response.jobId
          
          if (jobProgress.value && typeof jobProgress.value.show === 'function') {
            jobProgress.value.show()
          } else {
            toast.info('PDF generation started. Check job status manually.')
          }
        } else {
          // Synchronous response - download immediately
          console.log('DEBUG: Synchronous PDF generation response')
          const blob = new Blob([response], { type: 'application/pdf' })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = 'resume.pdf'
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)
          
          toast.success('PDF generated and downloaded!')
        }
      } catch (error) {
        console.error('DEBUG: PDF generation error:', error)
        toast.error('Failed to generate PDF: ' + error.message)
      } finally {
        pdfGenerating.value = false
      }
    }

    const handleJobCompleted = async (result) => {
      // Download the PDF when job completes
      try {
        if (!currentJobId.value) {
          console.error('No current job ID available for download')
          return
        }
        
        console.log('DEBUG: Job completed, downloading PDF for job:', currentJobId.value)
        
        // Store the job ID for later download
        lastGeneratedJobId.value = currentJobId.value
        lastGeneratedTimestamp.value = new Date().toISOString()
        
        // Download the PDF using the job result endpoint
        const pdfBuffer = await api.downloadJobResult(currentJobId.value)
        
        // Create and trigger download
        const blob = new Blob([pdfBuffer], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `resume-${currentJobId.value}.pdf`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
        
        toast.success('PDF downloaded successfully!')
      } catch (error) {
        console.error('DEBUG: Failed to download PDF:', error)
        toast.error('Failed to download PDF: ' + error.message)
      }
    }

    const handleJobFailed = (error) => {
      toast.error('PDF generation failed: ' + (error || 'Unknown error'))
    }

    const handleJobClosed = () => {
      currentJobId.value = ''
    }

    const downloadGeneratedPdf = async () => {
      if (!lastGeneratedJobId.value) {
        toast.error('No PDF has been generated yet')
        return
      }
      
      try {
        console.log('DEBUG: Downloading previously generated PDF for job:', lastGeneratedJobId.value)
        
        // Download the PDF using the stored job ID
        const pdfBuffer = await api.downloadJobResult(lastGeneratedJobId.value)
        
        // Create and trigger download
        const blob = new Blob([pdfBuffer], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `resume-${lastGeneratedJobId.value}.pdf`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
        
        toast.success('PDF downloaded successfully!')
      } catch (error) {
        console.error('DEBUG: Failed to download previously generated PDF:', error)
        toast.error('Failed to download PDF: ' + error.message)
        
        // If download fails, clear the stored job ID
        lastGeneratedJobId.value = ''
        lastGeneratedTimestamp.value = ''
      }
    }

    const handleTransformationSuccess = (result) => {
      console.log('[EditorView] Transformation success:', result)
      
      if (result.apply) {
        // Apply the transformation to the editor
        resumeMarkdown.value = result.transformedMarkdown
        toast.success('Funny transformation applied to resume!')
      } else {
        // Just show success message (preview already shown by component)
        console.log(`Transformation completed: ${result.transformedLength} chars, ${result.changesApplied} changes`)
      }
    }

    const handleTransformationError = (error) => {
      console.error('[EditorView] Transformation error:', error)
      toast.error(`Failed to transform resume: ${error.message}`)
    }

    const resetEditor = () => {
      resumeMarkdown.value = ''
      sessionId.value = ''
      selectedText.value = ''
      toast.info('Editor reset. Upload a new resume to continue.')
    }

    return {
      resumeMarkdown,
      sessionId,
      selectedText,
      currentJobId,
      pdfGenerating,
      jobProgress,
      lastGeneratedJobId,
      lastGeneratedTimestamp,
      showLegacyEditor,
      handleUploadSuccess,
      handleTextSelected,
      generatePdf,
      handleJobCompleted,
      handleJobFailed,
      handleJobClosed,
      handleTransformationSuccess,
      handleTransformationError,
      downloadGeneratedPdf,
      resetEditor,
      toggleLegacyEditor
    }
  }
}
</script>

<style scoped>
.editor-view {
  min-height: calc(100vh - 200px);
}

.upload-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  background: linear-gradient(135deg, var(--color-surface) 0%, var(--color-surface-muted) 100%);
  border-radius: var(--radius-lg);
  padding: 2rem;
  margin: 2rem 0;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--color-border);
}

.editor-header h2 {
  color: var(--color-text);
  font-size: 1.8rem;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.editor-layout {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

@media (max-width: 1024px) {
  .editor-layout {
    grid-template-columns: 1fr;
  }
}

.editor-column, .sidebar-column {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.editor-section, .sidebar-section {
  background: var(--color-surface-elevated);
  border-radius: var(--radius-md);
  padding: 1.5rem;
  box-shadow: var(--shadow-card);
  border: 1px solid var(--color-border);
}

.editor-section h3, .sidebar-section h3 {
  color: var(--color-text-muted);
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--color-border);
  font-size: 1.2rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: var(--radius-sm);
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: transform var(--transition-normal), box-shadow var(--transition-normal), background-color var(--transition-normal);
  font-size: 0.95rem;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--gradient-hero);
  color: var(--color-primary-on);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(2, 132, 199, 0.35);
}

.btn-secondary {
  background: var(--color-border);
  color: var(--color-text-muted);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-surface-muted);
}

.btn-suggestions {
  background: var(--gradient-hero);
  color: var(--color-primary-on);
}

.btn-suggestions:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(2, 132, 199, 0.35);
}

.btn-star {
  background: linear-gradient(135deg, #f6e05e 0%, #d69e2e 100%);
  color: var(--color-text);
}

.btn-star:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(245, 158, 11, 0.35);
}

.btn-success {
  background: var(--color-success);
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: var(--color-success-hover);
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(5, 150, 105, 0.35);
}

.funny-transform-btn {
  margin: 0;
}

.btn-icon {
  font-size: 1.1rem;
}

.session-info {
  background: var(--color-surface-muted);
  border-radius: var(--radius-sm);
  padding: 1rem;
  margin-top: 2rem;
  border: 1px solid var(--color-border);
  font-size: 0.9rem;
  color: var(--color-text-muted);
}

.session-info code {
  background: var(--color-surface);
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 0.85rem;
}

/* New streamlined flow styles (DeepSeek-inspired hero + dual CTAs) */
.streamlined-flow {
  max-width: 960px;
  margin: 0 auto;
  padding: 2rem 1rem 4rem;
}

/* Asymmetric hero: headline block offset left */
.flow-header {
  text-align: center;
  margin-bottom: 3.5rem;
}

.flow-tagline {
  font-family: var(--font-display);
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-primary);
  letter-spacing: 0.02em;
  margin-bottom: 0.75rem;
  text-transform: none;
}

.flow-title {
  font-family: var(--font-display);
  color: var(--color-text);
  font-size: 2.75rem;
  font-weight: 700;
  margin-bottom: 1rem;
  line-height: 1.2;
  background: var(--gradient-hero);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.flow-subtitle {
  color: var(--color-text-muted);
  font-size: 1.15rem;
  max-width: 560px;
  margin: 0 auto;
  line-height: 1.65;
}

/* Dual CTA cards (DeepSeek-style: Start Now | Access API) */
.flow-ctas {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 4rem;
}

@media (max-width: 768px) {
  .flow-ctas {
    grid-template-columns: 1fr;
  }
}

.cta-card {
  background: var(--color-surface-elevated);
  border-radius: var(--radius-lg);
  padding: 2rem;
  box-shadow: var(--shadow-card);
  border: 1px solid var(--color-border);
  transition: box-shadow var(--transition-normal), border-color var(--transition-normal);
  text-decoration: none;
  color: inherit;
}

.cta-card:hover {
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.1);
  border-color: var(--color-primary);
  text-decoration: none;
  color: inherit;
}

.cta-primary {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cta-secondary {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  text-decoration: none;
  color: inherit;
  min-height: 180px;
}

.cta-secondary:hover {
  color: inherit;
}

.cta-label {
  font-family: var(--font-display);
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.cta-desc {
  color: var(--color-text-muted);
  font-size: 0.95rem;
  line-height: 1.55;
  margin: 0;
}

.flow-content {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.flow-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.info-card {
  background: var(--color-surface-elevated);
  border-radius: var(--radius-lg);
  padding: 2rem;
  box-shadow: var(--shadow-card);
  border: 1px solid var(--color-border);
}

.info-card h3 {
  font-family: var(--font-display);
  color: var(--color-text);
  font-size: 1.3rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--color-primary);
}

.info-card ol, .info-card ul {
  color: var(--color-text-muted);
  line-height: 1.8;
  padding-left: 1.5rem;
}

.info-card li {
  margin-bottom: 0.5rem;
}

.info-card li:last-child {
  margin-bottom: 0;
}

.legacy-toggle {
  display: none;
  text-align: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid var(--color-border);
}

.btn-legacy {
  background: none;
  border: 1px solid var(--color-border);
  color: var(--color-text-muted);
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-md);
  font-size: 0.95rem;
  cursor: pointer;
  transition: background-color var(--transition-slow), border-color var(--transition-slow), color var(--transition-slow);
}

.btn-legacy:hover {
  background: var(--color-surface-muted);
  border-color: #a0aec0;
  color: var(--color-text-muted);
}

</style>
